import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';
const buttonVariants = cva('inline-flex font-gotham items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-white transition-colors border border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950 focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0', {
    variants: {
        variant: {
            default: 'bg-brand-orange text-xl text-[white!important] font-bold hover:border-brand-orange-3 hover:shadow-button-primary active:bg-cinnabarOrange-shade700 active:border-brand-orange active:shadow-button-primary disabled:bg-brand-rule-grey disabled:text-white [&>a]:text-white [&_*]:text-white',
            secondary: 'bg-white text-brand-orange border-brand-orange font-bold hover:shadow-button active:bg-brand-grey active:shadow-button disabled:text-brand-rule-grey disabled:border-brand-rule-grey',
            text: 'text-brand-navy disabled:text-brand-rule-grey-2',
            navy: 'bg-brand-navy text-white shadow-button active:border-brand-navy-2',
            icon: 'disabled:opacity-50',
        },
        size: {
            default: 'rounded-4xl',
            sm: 'rounded-4xl',
            lg: 'rounded-4xl',
            icon: 'p-2 rounded-md [&_svg]:size-3.5 sm:p-3.5 sm:rounded-lg sm:[&_svg]:size-6',
            md: 'rounded-4xl',
            ada: 'rounded-4xl',
        },
    },
    compoundVariants: [
        {
            variant: ['default', 'secondary', 'navy'],
            size: 'default',
            className: 'px-5 py-2 text-sm sm:px-8 sm:py-3.5 sm:text-xl sm:leading-6',
        },
        {
            variant: ['default', 'secondary', 'navy'],
            size: 'sm',
            className: 'px-5 py-2 text-sm',
        },
        {
            variant: 'text',
            size: 'sm',
            className: 'text-sm',
        },
        {
            variant: 'text',
            size: 'default',
            className: 'text-sm md:text-base',
        },
        {
            variant: 'text',
            size: 'lg',
            className: 'text-sm md:text-xl',
        },
        {
            variant: 'default',
            size: 'md',
            className: 'px-5 py-2 text-base',
        },
        {
            variant: ['default', 'secondary', 'navy'],
            size: 'ada',
            className: 'px-5 py-3 text-xl',
        },
    ],
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});
const Button = React.forwardRef(({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const passedAriaLabel = props['aria-label'];
    let computedAriaLabel = passedAriaLabel;
    if (!passedAriaLabel) {
        const childText = typeof children === 'string'
            ? children.trim()
            : Array.isArray(children)
                ? children
                    .filter((c) => typeof c === 'string')
                    .join(' ')
                    .trim()
                : '';
        // 💡 Add context where needed
        if (childText.toLowerCase() === 'add') {
            computedAriaLabel = 'Add to Cart';
        }
        else if (childText.length > 0) {
            computedAriaLabel = childText;
        }
    }
    return (React.createElement(Comp, { ref: ref, className: cn(buttonVariants({ variant, size }), className), "aria-label": computedAriaLabel, ...props }, children));
});
Button.displayName = 'Button';
export { Button, buttonVariants };
